import { type InputCapture } from '@canvas/input';
import { type Capture } from '@canvas/input/types';

export type EventSubscriber = (events: Capture[]) => void;

export interface SharedEventLoop {
  subscribe(fn: EventSubscriber): () => void;
  start(): void;
  stop(): void;
}

/**
 * Creates a single shared event loop that allows us to share among keyboard and mouse.
 */
export function createSharedEventLoop(inputCapture: InputCapture): SharedEventLoop {
  const subscribers = new Set<EventSubscriber>();
  let frameId: number | null = null;

  function processFrame() {
    const events = inputCapture.getEvents();

    // Broadcast all events to all subscribers (Mouse, Keyboard, etc.)
    if (events.length > 0) {
      subscribers.forEach((fn) => fn(events));
    }

    // Safely clear the queue exactly once per frame
    inputCapture.clear();

    frameId = requestAnimationFrame(processFrame);
  }

  return {
    subscribe(fn: EventSubscriber) {
      subscribers.add(fn);

      return () => subscribers.delete(fn);
    },

    start() {
      if (frameId !== null) {
        return;
      }

      frameId = requestAnimationFrame(processFrame);
    },

    stop() {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);

        frameId = null;
      }
    },
  };
}
